import {
  RefObject,
  ComponentType,
  UIEvent,
  WheelEvent as ReactWheelEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';
import { ChartWindowState, ChartDimensions } from '@ardoq/graph';
import { Excluded, getScrollbarWidth } from '@ardoq/common-helpers';
import { clamp, throttle } from 'lodash';
import { getChartWindowScrollLeftForWindowCenterX } from './calculations';
import { DEFAULT_WINDOW_CENTER } from '../consts';
import { TimelineViewCommands, WindowScalingLimitsForGrid } from '../types';
import { useEffectOnce } from '@ardoq/hooks';

const WINDOW_UPDATE_DELAY = 100;
const ZOOM_STEP = 0.2;

const ScrollDiv = styled.div<{ $height: number }>`
  position: absolute;
  bottom: 0;
  z-index: 5;
  overflow-x: scroll;

  ::-webkit-scrollbar:horizontal {
    -webkit-appearance: none;
    height: ${({ $height }) => $height}px;
  }
`;

const WheelEventCatcher = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
  overflow: hidden;
`;

type PanAndZoomScrollbarInjectedProps = {
  containerRef?: RefObject<HTMLDivElement>;
};

type PanAndZoomScrollbarProps = ChartWindowState & {
  timelineChartProperties: {
    dimensions: ChartDimensions;
  };
  viewInstanceId: string;
  isPanning: boolean;
  windowScalingLimitsForGrid: WindowScalingLimitsForGrid;
  commands: TimelineViewCommands;
};

const panAndZoomScrollbar =
  <P extends PanAndZoomScrollbarInjectedProps>(
    WrappedComponent: ComponentType<P>
  ): ComponentType<
    Excluded<P, PanAndZoomScrollbarInjectedProps> & PanAndZoomScrollbarProps
  > =>
  (props: PanAndZoomScrollbarProps) => {
    const {
      windowScale,
      timelineChartProperties: { dimensions },
      windowCenter,
      isPanning,
      viewInstanceId,
      windowScalingLimitsForGrid,
      commands,
    } = props;
    const throttledSetScrollPosition = useMemo(
      () =>
        throttle(commands.setChartWindowScrollPosition, WINDOW_UPDATE_DELAY),
      [commands]
    );
    const scrollbarSize = useMemo(() => getScrollbarWidth() || 15, []);
    const chartWidth = dimensions.chart.width;
    const scrollRef = useRef<HTMLDivElement>(null);
    const wheelCatcherRef = useRef<HTMLDivElement>(null);
    const windowScaleRef = useRef(windowScale);
    const windowCenterRef = useRef(windowCenter);
    const windowScalingLimitsRef = useRef(windowScalingLimitsForGrid);

    windowScaleRef.current = windowScale;
    windowCenterRef.current = windowCenter;
    windowScalingLimitsRef.current = windowScalingLimitsForGrid;

    useEffect(() => {
      const container = wheelCatcherRef.current;
      if (!container) {
        return;
      }

      const handleWheel = (e: WheelEvent) => {
        if (!e.ctrlKey) {
          return;
        }
        e.preventDefault();
        const zoomFactor = 1 + clamp(e.deltaY / 100, -1, 1) * ZOOM_STEP;
        const newWindowScale = clamp(
          windowScaleRef.current * zoomFactor,
          windowScalingLimitsRef.current.min,
          windowScalingLimitsRef.current.max
        );
        commands.setChartWindow(
          {
            windowScale: newWindowScale,
            windowCenter:
              newWindowScale >= windowScalingLimitsRef.current.max
                ? DEFAULT_WINDOW_CENTER
                : windowCenterRef.current,
          },
          viewInstanceId
        );
      };

      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }, [viewInstanceId, commands]);

    /**
     * Currently, element.scrollLeft does not handle sub-pixel values, it floors them.
     * At small scales the discarded values are needed for window center calculation
     */
    const preciseScrollLeftValue = useMemo(
      () =>
        getChartWindowScrollLeftForWindowCenterX(
          chartWidth,
          windowCenter[0],
          windowScale
        ),
      [chartWidth, windowCenter, windowScale]
    );

    /**
     * We must not mutate the DOM with the scrollLeft value when the user is
     * dragging the scrollbar, otherwise it will result in jerky behavior.
     *
     * But we must set the scollbar position when the window center is updated
     * by whatever else from outside. And also initially.
     */

    useEffectOnce(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = preciseScrollLeftValue;
      }
    });

    /**
     * If the user is dragging the scrollbar, do not set the scrollRef.current.scrollLeft
     */
    const isUserDraggingTheScrollbarRef = useRef(false);

    if (
      !isUserDraggingTheScrollbarRef.current &&
      scrollRef.current &&
      Math.floor(scrollRef.current.scrollLeft) !==
        Math.floor(preciseScrollLeftValue)
    ) {
      scrollRef.current.scrollLeft = preciseScrollLeftValue;
    }

    const onScroll = (event: UIEvent<HTMLDivElement>) => {
      if (isPanning || !(event.target instanceof HTMLDivElement)) {
        return;
      }

      /**
       * Event.target.scrollLeft is floored => will produce imprecise results. Unfortunately, we have to use it,
       * when the chart window position is determined by scrolling. However, when scrolling is a result of setting
       * the chart window position, we must avoid overwriting the set value with the imprecise result.
       *
       * To find out if the scroll event is an action or a reaction, we can check if the event.target.scrollLeft
       * is the floored preciseScrollLeftValue.
       */
      const scrollLeft = event.target.scrollLeft;
      if (Math.floor(scrollLeft) === Math.floor(preciseScrollLeftValue)) {
        return;
      }

      throttledSetScrollPosition(scrollLeft, viewInstanceId);
    };

    const handleChartAreaWheel = (e: ReactWheelEvent) => {
      e.stopPropagation();

      if (
        !scrollRef.current ||
        !chartWidth ||
        !((e.shiftKey && e.deltaY) || e.deltaX)
      ) {
        return;
      }

      const scrollLeft = preciseScrollLeftValue + (e.deltaX || e.deltaY);

      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollLeft;
      }

      throttledSetScrollPosition(scrollLeft, viewInstanceId);
    };

    return (
      <WheelEventCatcher
        ref={wheelCatcherRef}
        onWheel={handleChartAreaWheel}
        style={windowScale < 1 ? { paddingBottom: scrollbarSize } : undefined}
      >
        <WrappedComponent {...(props as any)} />
        <ScrollDiv
          onScroll={onScroll}
          onMouseDown={() => {
            isUserDraggingTheScrollbarRef.current = true;
          }}
          onMouseUp={() => {
            isUserDraggingTheScrollbarRef.current = false;
          }}
          ref={scrollRef}
          $height={scrollbarSize}
          style={{
            display: windowScale < 1 ? 'block' : 'none',
            maxWidth: chartWidth,
            minWidth: chartWidth,
            right: dimensions.margin.right,
          }}
        >
          <div
            style={{
              height: scrollbarSize,
              width: chartWidth * (2 - windowScale),
            }}
          />
        </ScrollDiv>
      </WheelEventCatcher>
    );
  };

export { panAndZoomScrollbar };
